'use client'

import {Button} from "@/components/ui/button";
import {ReactEventHandler, useCallback, useState, useTransition} from "react";
import {Input} from "@/components/ui/input";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "@/components/ui/card";
import Link from "next/link";
import {Badge} from "@/components/ui/badge";
import {getScoreLabel} from "@/lib/score";
import {getTailwindGradientColor} from "@/lib/utils";
import {InformationCircleIcon, MagnifyingGlassIcon} from "@heroicons/react/24/outline";
import {ArrowRight, FilterIcon, Loader2} from "lucide-react";
import {useRouter, useSearchParams} from "next/navigation";
import {Skeleton} from "@/components/ui/skeleton";
import {formatDistanceToNowStrict} from "date-fns";

export function StorySection({ title, description, articles }: { title: string, description?: string, articles: any }) {
  if (!articles || articles.length === 0) {
    return (
      <section className="py-24">
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <h2 className="text-4xl font-semibold flex gap-4"><span>🔍🙈</span><span>No results found</span></h2>
          <div className="text-muted-foreground text-sm text-center">
            We couldn’t find any content matching your request. <br/>
            Try adjusting your filters or search terms.
          </div>
        </div>
      </section>
    )
  }
  return (
    <section className="mb-10">
      <h2 className="text-2xl font-semibold">{title}</h2>
      <p className="text-muted-foreground mb-6">{description}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {articles.map((article: any, index: number) => (
          <StoryCard key={index} content={article} />
        ))}
      </div>
      {/*{visible < articles.length && (*/}
      {/*  <div className="pt-4">*/}
      {/*    <Button variant="link" onClick={() => setVisible(visible + 6)}>*/}
      {/*      Load More*/}
      {/*    </Button>*/}
      {/*  </div>*/}
      {/*)}*/}
    </section>
  );
}

export function LoadingContent () {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 pt-10 gap-8 space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-8 p-4 rounded-xl">
          <Skeleton className="h-10 w-1/2 rounded-lg" />
          <Skeleton className="h-6 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
          <div className="flex gap-2 justify-end">
            <Skeleton className="h-8 w-28 rounded-lg" />
            <Skeleton className="h-8 w-28 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function StoryFilter({ techniques }: { techniques?: any[] | null }) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [query, setQuery] = useState(searchParams.get('query') || '');
  const [minScore, setMinScore] = useState(searchParams.get('min_score') || '');
  const [maxScore, setMaxScore] = useState(searchParams.get('max_score') || '');
  const [technique, setTechnique] = useState(searchParams.get('technique') || '');
  const [isPending, startTransition] = useTransition();

  const [isFiltersOpen, setFiltersOpen] = useState(false);

  const applyFilter = useCallback<ReactEventHandler>((e) => {
    if (e) {
      e.preventDefault();
    }

    const params = new URLSearchParams(searchParams.toString());
    const filters: any = { query, max_score: maxScore, min_score: minScore, technique };
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    })
    setFiltersOpen(false);
    startTransition(() => {
      router.push(`/explore/search?${params.toString()}`);
    })
  }, [query, minScore, maxScore, technique, searchParams, router]);

  const clearFilter = useCallback(() => {
    setQuery('');
    setMinScore('');
    setMaxScore('');
    setTechnique('');
    setFiltersOpen(false);
    startTransition(() => {
      router.push('/explore/search');
    })
  }, [router])


  return (
    <>
      <form className="flex flex-wrap gap-4 mb-10">
        <Input
          placeholder="Search headline..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1"
        />
        <Button className="flex items-center gap-1" type="submit" onClick={applyFilter} disabled={isPending}>{isPending ? <Loader2 className="animate-spin" /> : <MagnifyingGlassIcon />}<span className="hidden md:inline">Search</span></Button>
        <Popover open={isFiltersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline"><span className="hidden md:inline">Filters</span> <FilterIcon /></Button>
          </PopoverTrigger>
          <PopoverContent className="space-y-4">
            <div className="w-full space-y-2">
              <label className="text-sm font-medium">Technique</label>
              <Select value={technique || "all"} onValueChange={(value) => setTechnique(value === "all" ? '' : value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Technique" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Techniques</SelectItem>
                  {techniques && techniques.map((technique) => (
                    <SelectItem key={technique.id} value={technique.id}>{technique.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Score Range</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={maxScore}
                  onChange={(e) => setMaxScore(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={clearFilter}>Clear</Button>
              <Button type="submit" onClick={applyFilter}>Apply Filter</Button>
            </div>
          </PopoverContent>
        </Popover>
      </form>
      {isPending && <LoadingContent />}
    </>
  )
}

export function StoryCard({ content }: { content: any  }) {
  const scoreLabel = getScoreLabel(content.propaganda_score, true);
  return (
    <Card className="w-full justify-between">
      <CardHeader>
        <CardTitle>{content.headline}</CardTitle>
        <CardDescription className="font-semibold flex gap-1">
          <span>
            {content.source.type === 'social_network' && content.authors.length > 0 ? `${content.authors[0].author.name} (${content.source.name}) ` : content.source.name}
          </span>
          {content.published_at && (
            <>
              <span>
                &#x2022;
              </span>
              <time dateTime={content.published_at}>
                {formatDistanceToNowStrict(new Date(content.published_at), { addSuffix: true })}
              </time>
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-2 items-end mb-8">
          <div className="text-sm font-semibold">Score:</div>
          <div className="flex-1 flex items-end gap-1 text-sm">
            <div className="text-3xl font-bold leading-none" style={{ color: getTailwindGradientColor(content.propaganda_score) }}>
              {content.propaganda_score}
            </div>
            <div>/</div>
            <div>100</div>
          </div>
          <div className="flex items-center gap-1">
            <div className="font-bold">{scoreLabel.emoji}&nbsp;{scoreLabel.label}</div>
            <Popover>
              <PopoverTrigger className="cursor-pointer"><InformationCircleIcon className="size-3"/></PopoverTrigger>
              <PopoverContent className="text-sm">
                {scoreLabel.description}
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="text-sm font-semibold mb-4">
          Detected Techniques:
        </div>
        <div className="flex flex-wrap gap-2">
          {content.techniques.length > 0
          ? (
            <>
              {content.techniques.slice(0, 4).map((tech: any, i: number) => (
                <Badge key={i} variant="outline">
                  {tech.technique.name}
                </Badge>
              ))}
              {content.techniques.length > 5 && <span className="text-sm">and others...</span>}
            </>
          ) : (
            <i className="text-muted-foreground text-sm">No techniques used</i>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex gap-4 justify-end">
        {content.url && (
          <Button variant="link" asChild>
            <Link target="_blank" href={content.url} rel="noopener noreferrer">
              Go To Original
            </Link>
          </Button>
        )}
        <Button asChild>
          <Link href={`/reports/${content.url_hash}`}>
            View Analysis <ArrowRight />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  )
}
